import type { ImageBlock } from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import { Node } from "@tiptap/core"
import type { Node as ProsemirrorNode, Schema } from "@tiptap/pm/model"
import { ReactNodeViewRenderer } from "@tiptap/react"

import type { ImageBlockData } from "../../../../core/imageUtils"
import { ImageExtension } from "../../models"
import { ImageView, imageViewHeight, nodeWrapperClassName } from "./ImageView"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    image: {
      setImage: () => ReturnType
    }
  }
}

interface Attributes {
  content: { default: ImageBlockData | undefined }
}

const component = Node.create({
  name: "image",
  group: "block",
  atom: true,

  addAttributes(): Attributes {
    return {
      content: {
        default: undefined,
      },
    }
  },

  addCommands() {
    return {
      setImage: () => (props) => {
        const { dispatch, tr } = props

        const node = this.type.create()
        if (dispatch) {
          tr.replaceSelectionWith(node)
        }

        return true
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "image",
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["image", HTMLAttributes]
  },

  addNodeView() {
    return function renderer(props) {
      const empty = props.node.attrs["content"] === undefined

      return ReactNodeViewRenderer(ImageView, {
        attrs: {
          // Set the initial height on the node wrapper as tiptap doesn't render the React component immediately,
          // which causes a layout shift. Note that this only takes care of the initial height as addNodeView is only called once.
          style: `min-height: ${imageViewHeight(empty)}px`,
          class: nodeWrapperClassName,
        },
      })(props)
    }
  },
})

const converter = {
  fromPrismic(
    schema: Schema,
  ): (image: ImageBlock) => ProsemirrorNode | undefined {
    return function (image: ImageBlock): ProsemirrorNode | undefined {
      return schema.nodes[component.name]?.create({
        content: image.data,
      })
    }
  },

  toPrismic(node: ImageNode): ImageBlock | undefined {
    if (node.attrs.content === undefined) return undefined

    return {
      type: "image",
      data: {
        ...node.attrs.content,
      },
    }
  },
}

interface ImageNode extends ProsemirrorNode {
  attrs: {
    content: ImageBlockData | undefined
  }
}

// eslint-disable-next-line import/no-default-export
export default new ImageExtension(
  [RichTextNodeType.image],
  component.name,
  component,
  converter,
)
